'use client';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, Typography } from '@mui/material';

import { ComponentErrorBoundary } from '@/components';
import { useT } from '@/hooks';
import { DayEarnings, ProcessedEarningEvent } from '@/types/earnings.types';
import { EarningCard } from './EarningCard';
import { NoEarnings } from './NoEarnings';

interface DesktopEarningsProps {
  days: DayEarnings[];
  onPrev: () => void;
  onNext: () => void;
  prevDisabled: boolean;
  nextDisabled: boolean;
}

export const DesktopEarnings = ({
  days,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
}: DesktopEarningsProps) => {
  const { t } = useT();
  if (!days || !Array.isArray(days)) {
    return null;
  }

  const renderEventItem = (day: DayEarnings, event: ProcessedEarningEvent, index: number) => {
    const eventType = event.type;
    const isFirstOfType = eventType && index === day.events.findIndex((e) => e.type === eventType);

    return (
      <Box key={event.id || index}>
        {isFirstOfType && (
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              fontWeight: 600,
              mb: 1.5,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {eventType}
          </Typography>
        )}
        <EarningCard event={event} variant="desktop" />
      </Box>
    );
  };

  const renderDayColumn = (day: DayEarnings) => {
    if (!day) return null;

    const eventsContent =
      !day.events || day.events.length === 0 ? (
        <NoEarnings />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {day.events.map((event, index) => renderEventItem(day, event, index))}
        </Box>
      );

    return (
      <ComponentErrorBoundary
        key={`${day.day}-${day.date}`}
        componentName={`Day Column - ${day.day} ${day.date}`}
        fallback={
          <Box
            sx={{
              flex: 1,
              minWidth: 200,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'error.main',
            }}
          >
            {t('common.error')}
          </Box>
        }
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 200,
            height: 400,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: day.isToday ? 'action.hover' : 'transparent',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{ fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500, mb: 0.5 }}
            >
              {day?.day || ''}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: day.isToday ? 'primary.main' : 'transparent',
                color: day.isToday ? 'primary.contrastText' : 'text.primary',
                fontSize: '1.125rem',
                fontWeight: 700,
              }}
            >
              {day?.date || ''}
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'action.hover',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                bgcolor: 'action.selected',
              },
            }}
          >
            {eventsContent}
          </Box>
        </Box>
      </ComponentErrorBoundary>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 3,
        overflowX: 'auto',
      }}
    >
      <IconButton
        onClick={onPrev}
        disabled={prevDisabled}
        sx={{
          width: 40,
          height: 40,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          flexShrink: 0,
          color: 'text.secondary',
          '&:disabled': {
            opacity: 0.5,
          },
        }}
      >
        <ChevronLeftIcon />
      </IconButton>

      <Box sx={{ display: 'flex', gap: 2, flex: 1, minWidth: 0 }}>{days.map(renderDayColumn)}</Box>

      <IconButton
        onClick={onNext}
        disabled={nextDisabled}
        sx={{
          width: 40,
          height: 40,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          flexShrink: 0,
          color: 'text.secondary',
          '&:disabled': {
            opacity: 0.5,
          },
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};
